"use client";

import { useEffect, useState } from "react";

interface LoadingOverlayProps {
  isVisible: boolean;
}

const LOADING_MESSAGES = [
  { lang: "en", text: "Loading..." },
  { lang: "pt", text: "Carregando..." },
  { lang: "es", text: "Cargando..." },
  { lang: "fr", text: "Chargement..." },
  { lang: "de", text: "Laden..." },
  { lang: "it", text: "Caricamento..." },
  { lang: "zh", text: "加载中..." },
  { lang: "ja", text: "読み込み中..." },
  { lang: "ar", text: "جارٍ التحميل..." },
];

export function LoadingOverlay({ isVisible }: LoadingOverlayProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [fillLevel, setFillLevel] = useState(0.2);

  useEffect(() => {
    if (!isVisible) return;

    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 800);

    const fillInterval = setInterval(() => {
      setFillLevel((prev) => {
        const next = prev + 0.1;
        return next > 0.9 ? 0.2 : next;
      });
    }, 300);

    return () => {
      clearInterval(messageInterval);
      clearInterval(fillInterval);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const currentMessage = LOADING_MESSAGES[messageIndex];
  const isRTL = currentMessage.lang === "ar";

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-6">
        <div className="relative">
          <svg 
            className="w-24 h-24 text-primary animate-float" 
            viewBox="0 0 80 80" 
            fill="none"
          >
            <defs>
              <linearGradient id="loadingBucketFill" x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
                <stop offset={`${fillLevel * 100}%`} stopColor="currentColor" stopOpacity="0.4" />
                <stop offset={`${fillLevel * 100}%`} stopColor="transparent" />
              </linearGradient>
              <linearGradient id="loadingHeartGlow" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0.7" />
              </linearGradient>
            </defs>
            <path 
              d="M16 24h48v36a8 8 0 01-8 8H24a8 8 0 01-8-8V24z" 
              fill="url(#loadingBucketFill)" 
              stroke="currentColor" 
              strokeWidth="2.5"
              className="opacity-90"
            />
            <path 
              d="M12 18a4 4 0 014-4h48a4 4 0 014 4v6H12v-6z" 
              fill="currentColor" 
              fillOpacity="0.15"
              stroke="currentColor" 
              strokeWidth="2.5"
            />
            <path 
              d="M26 8c0-2 2-4 4-4h20c2 0 4 2 4 4v6H26V8z" 
              stroke="currentColor" 
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
            <g className="animate-pulse" style={{ transformOrigin: '40px 44px' }}>
              <path 
                d="M40 34c-4-4-10-3-10 2 0 4 6 10 10 14 4-4 10-10 10-14 0-5-6-6-10-2z" 
                fill="url(#loadingHeartGlow)"
              />
            </g>
            <circle 
              cx="28" cy="48" r="2" 
              fill="currentColor" 
              fillOpacity="0.3" 
              className="animate-ping" 
              style={{ animationDuration: '2s' }} 
            />
            <circle 
              cx="52" cy="42" r="1.5" 
              fill="currentColor" 
              fillOpacity="0.3" 
              className="animate-ping" 
              style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} 
            />
            <circle 
              cx="36" cy="54" r="1.5" 
              fill="currentColor" 
              fillOpacity="0.3" 
              className="animate-ping" 
              style={{ animationDuration: '3s', animationDelay: '1s' }} 
            />
          </svg>
        </div>

        <p 
          className="text-lg font-medium text-muted-foreground animate-pulse"
          dir={isRTL ? "rtl" : "ltr"}
        >
          {currentMessage.text}
        </p>
      </div>
    </div>
  );
}
